import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setActiveScope } from '../utils/api';
import { chooseOrganization, chooseProject, readSavedScope, STORAGE_KEY } from '../utils/scopeHelpers';

const ScopeContext = createContext(null);

export const ScopeProvider = ({ enabled, children }) => {
  const [state, setState] = useState({
    status: enabled ? 'loading' : 'idle',
    organizations: [],
    projects: [],
    organization: null,
    project: null,
    error: null,
    version: 0,
  });

  const applyScope = useCallback((organization, project, organizations, projects) => {
    const scope = { organizationId: organization.id, projectId: project.id };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
    setActiveScope(scope);
    setState((current) => ({
      status: 'ready', organizations, projects, organization, project, error: null,
      version: current.version + 1,
    }));
  }, []);

  const load = useCallback(async () => {
    if (!enabled) {
      setActiveScope(null);
      setState({ status: 'idle', organizations: [], projects: [], organization: null, project: null, error: null, version: 0 });
      return;
    }
    setActiveScope(null);
    setState((current) => ({ ...current, status: 'loading', error: null, organization: null, project: null }));
    try {
      const saved = readSavedScope();
      const organizationResponse = await api.getOrganizations();
      const organizations = organizationResponse.data.organizations || [];
      const organization = chooseOrganization(organizations, saved);
      if (!organization) {
        window.localStorage.removeItem(STORAGE_KEY);
        setState((current) => ({ ...current, status: 'empty', organizations: [], projects: [], organization: null, project: null }));
        return;
      }
      const projectResponse = await api.getProjects(organization.id);
      const projects = projectResponse.data.projects || [];
      const project = chooseProject(projects, saved, organization.id);
      if (!project) {
        window.localStorage.removeItem(STORAGE_KEY);
        setState((current) => ({ ...current, status: 'empty', organizations, projects: [], organization, project: null }));
        return;
      }
      applyScope(organization, project, organizations, projects);
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;
      setState((current) => ({ ...current, status: 'error', error: 'Your project scope could not be loaded.' }));
    }
  }, [applyScope, enabled]);

  useEffect(() => { load(); }, [load]);

  const selectOrganization = useCallback(async (organizationId) => {
    const organization = state.organizations.find((item) => item.id === organizationId);
    if (!organization) return;
    setActiveScope(null);
    setState((current) => ({ ...current, status: 'loading', organization: null, project: null }));
    try {
      const response = await api.getProjects(organization.id);
      const projects = response.data.projects || [];
      if (!projects.length) {
        window.localStorage.removeItem(STORAGE_KEY);
        setState((current) => ({ ...current, status: 'empty', organization, projects: [], project: null }));
        return;
      }
      applyScope(organization, projects[0], state.organizations, projects);
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') {
        setState((current) => ({ ...current, status: 'error', error: 'That organization could not be opened.' }));
      }
    }
  }, [applyScope, state.organizations]);

  const selectProject = useCallback((projectId) => {
    const project = state.projects.find((item) => item.id === projectId);
    if (state.organization && project) {
      applyScope(state.organization, project, state.organizations, state.projects);
    }
  }, [applyScope, state.organization, state.organizations, state.projects]);

  const value = useMemo(() => ({ ...state, retry: load, selectOrganization, selectProject }), [state, load, selectOrganization, selectProject]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

export const useScope = () => {
  const value = useContext(ScopeContext);
  if (!value) throw new Error('useScope must be used within ScopeProvider');
  return value;
};

